import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { createAsset } from '../../api/operatingCosts'
import AccountSelect, { useAccounts } from '../../components/AccountSelect'
import ErrorBanner from '../../components/ErrorBanner'
import { HelpTip, LabelWithHelp } from '../../components/HelpTip'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { num } from '../sales/salesShared'
import AssetFields, { assetToForm, checkAsset } from './AssetFields'
import { useControlAccounts, useStandardDefaults } from './accountingShared'

// Add a fixed asset and start its depreciation schedule; optionally post
// the purchase entry too.
export default function AssetForm() {
  const nav = useNavigate()
  const { notify } = useToast()
  const accounts = useAccounts()
  const controls = useControlAccounts()
  const std = useStandardDefaults()
  const [form, setForm] = useState(() => ({ ...assetToForm(), acquisition_date: localToday() }))
  const [recordNow, setRecordNow] = useState(false)
  const [creditCode, setCreditCode] = useState('')
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  // Start on the company's own accounts (its control accounts), else the
  // usual codes when they exist in its chart.
  useEffect(() => {
    if (!controls || !accounts.length) return
    const has = (c) => c && accounts.some((a) => a.account_code === c)
    const pick = (...codes) => codes.find(has) || ''
    setForm((f) => ({
      ...f,
      asset_account_code: f.asset_account_code || pick(controls.PPE, std.PPE),
      accum_dep_account_code: f.accum_dep_account_code || pick(controls.AccumDep, std.AccumDep),
      expense_account_code: f.expense_account_code || pick(controls.DepreciationExpense, std.DepreciationExpense),
    }))
    setCreditCode((c) => c || pick(controls.Cash, std.Cash))
  }, [controls, accounts, std])

  const save = async () => {
    setError('')
    const e = checkAsset(form)
    if (recordNow && !creditCode) e.credit = 'Choose the account it was paid from.'
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    try {
      const res = await createAsset({
        name: form.name.trim(),
        category: form.category.trim(),
        acquisition_date: form.acquisition_date,
        location_id: form.location_id || null,
        cost: num(form.cost),
        salvage_value: num(form.salvage_value),
        useful_life_months: parseInt(form.useful_life_months, 10),
        frequency: 'Monthly',
        asset_account_code: form.asset_account_code,
        accum_dep_account_code: form.accum_dep_account_code,
        expense_account_code: form.expense_account_code,
        vendor_id: form.vendor_id || null,
        custodian_user_id: form.custodian_user_id || null,
        department: form.department.trim(),
        serial_number: form.serial_number.trim(),
        model: form.model.trim(),
        manufacturer: form.manufacturer.trim(),
        capitalization_source: form.capitalization_source,
        record_acquisition_now: recordNow,
        ...(recordNow ? { credit_account_code: creditCode } : {}),
      })
      notify(res?.je_id ? `${form.name.trim()} added, purchase posted as ${docNo('je', res.je_no)}` : `${form.name.trim()} added`)
      nav(`/accounting/assets/${res.asset_id}`, { replace: true })
    } catch (err) {
      setError(err.message || 'Could not save the asset.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/accounting/assets" label="Fixed assets" />} title={<>Add a fixed asset <HelpTip k="fixedAssets" /></>} />

      {error && <ErrorBanner message={error} />}

      <AssetFields form={form} set={set} errors={errors} />

      <div className="ci-form-meta-card">
        <label className="ci-form-toggle acc-toggle">
          <input type="checkbox" checked={recordNow} onChange={(e) => setRecordNow(e.target.checked)} />
          <LabelWithHelp label="Post the purchase entry now" k="acquisitionEntry" />
        </label>
        {recordNow && (
          <div className="ci-form-meta-top two acc-tight">
            <label className="ci-form-field">
              <span>Paid from (credit)</span>
              <AccountSelect value={creditCode} onChange={setCreditCode} types={['Asset', 'Liability']} />
              {errors.credit && <span className="acc-field-error">{errors.credit}</span>}
            </label>
          </div>
        )}
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Check size={14} />{saving ? 'Saving…' : 'Add asset'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
